import {
  $createTextNode,
  $getRoot,
  $getState,
  $setState,
  type LexicalNode,
} from 'lexical';

import type { DocumentSectionSpec, NoteEditorBlockDef } from '../../config';
import {
  $createTemplateFieldNode,
  $createTemplateHeadingNode,
  $createTemplateInstructionNode,
  $createTemplateParagraphNode,
  $isTemplateHeadingNode,
  $isTemplateInstructionNode,
  $isTemplateParagraphNode,
  templateInstructionRoleState,
  templateLineState,
  templateSectionState,
  templateWordingState,
  type TemplateHeadingNode,
  type TemplateInstructionNode,
  type TemplateLineDef,
  type TemplateParagraphNode,
} from '../../ui/TemplateEditor/nodes';
import { createTempId } from '../createTempId';

// What the template document refuses to save, by `TemplateIssue.Kind`.
export type TemplateDocumentIssueKind =
  | 'content_before_section'
  | 'duplicate_id'
  | 'unknown_node'
  | 'line_break_in_line';

export const createTemplateTextDef = (
  ID: string,
  Value = '',
): NoteEditorBlockDef => ({
  ID,
  SlotID: '',
  Kind: { case: 'Text', value: { Value } },
});

export const createTemplateSlotDef = (
  instruction = '',
): NoteEditorBlockDef => ({
  ID: createTempId(),
  SlotID: '',
  AIInstruction: instruction || undefined,
  Kind: { case: 'FreeText', value: {} },
});

const textOf = (def: NoteEditorBlockDef) =>
  def.Kind.case === 'Text' ? def.Kind.value.Value : '';

export const isEmptyTemplateText = (def: NoteEditorBlockDef) =>
  def.Kind.case === 'Text' && !def.Kind.value.Value;

// The note prints no space before punctuation.
const startsWithPunctuation = (text: string) => /^[.,;:%)]/.test(text);

type LinePart =
  | { kind: 'text'; def: NoteEditorBlockDef; text: string }
  | { kind: 'block'; def: NoteEditorBlockDef }
  | { kind: 'space' };

// A line's blocks as its text: a space goes where the note prints one, at
// the end of the text before it when there is text, else on its own.
const toLineParts = (blocks: NoteEditorBlockDef[]) =>
  blocks.reduce<LinePart[]>((parts, def) => {
    if (isEmptyTemplateText(def)) {
      return parts;
    }
    const spaced = parts.length > 0 && !startsWithPunctuation(textOf(def));
    if (def.Kind.case === 'Text') {
      return [
        ...parts,
        { kind: 'text', def, text: `${spaced ? ' ' : ''}${textOf(def)}` },
      ];
    }
    const last = parts.at(-1);
    if (spaced && last?.kind === 'text') {
      return [
        ...parts.slice(0, -1),
        { ...last, text: `${last.text} ` },
        { kind: 'block', def },
      ];
    }
    return [
      ...parts,
      ...(spaced ? [{ kind: 'space' } as const] : []),
      { kind: 'block', def },
    ];
  }, []);

export const $createTemplateWording = (
  def: NoteEditorBlockDef,
  text: string,
  lineId: string,
) => $setState($createTextNode(text), templateWordingState, { def, lineId });

// A block that is not text: a free-text block is an AI instruction, any
// other a field.
export const $createTemplateBlockNode = (def: NoteEditorBlockDef) =>
  def.Kind.case === 'FreeText'
    ? $createTemplateInstructionNode(def)
    : $createTemplateFieldNode(def);

const $createLinePart = (part: LinePart, lineId: string): LexicalNode => {
  switch (part.kind) {
    case 'text':
      return $createTemplateWording(part.def, part.text, lineId);
    case 'block':
      return $createTemplateBlockNode(part.def);
    default:
      return $createTextNode(' ');
  }
};

// A line is one paragraph: its text blocks are its text, its other blocks
// fields and instructions in it.
export const $createTemplateLine = (line: TemplateLineDef) =>
  $setState($createTemplateParagraphNode(), templateLineState, line).append(
    ...toLineParts(line.Blocks).map((part) => $createLinePart(part, line.ID)),
  );

export const createTemplateLineDef = (
  ShowWhen: TemplateLineDef['ShowWhen'] = [],
): TemplateLineDef => ({ ID: createTempId(), Blocks: [], ShowWhen });

export const $isSectionGuidance = (
  node: LexicalNode | null | undefined,
): node is TemplateInstructionNode =>
  $isTemplateInstructionNode(node) &&
  $getState(node, templateInstructionRoleState) === 'section';

export const $createSectionGuidance = (instructions = '') =>
  $setState(
    $createTemplateInstructionNode(createTemplateSlotDef(instructions)),
    templateInstructionRoleState,
    'section',
  );

// A section's instructions, when it has any, are its guidance alone on the
// first line under its heading; a field of a summary or an evaluation has
// that line even while its instruction is empty.
const $createGuidanceLines = (
  instructions: string | undefined,
  fields: boolean,
) => {
  if (!instructions?.trim()) {
    return fields ? [$createTemplateParagraphNode()] : [];
  }
  return [
    $createTemplateParagraphNode().append($createSectionGuidance(instructions)),
  ];
};

// A section's guidance as stored: an emptied one is no instructions, as one
// deleted is, but for instructions read as empty, which stay so.
export const readSectionGuidance = (
  guidance: TemplateInstructionNode | undefined,
  stored: string | undefined,
) => {
  if (!guidance) {
    return stored?.trim() ? undefined : stored;
  }
  const text = guidance.getDef().AIInstruction ?? '';
  return text.trim() ? text : stored === '' ? '' : undefined;
};

export const $createTemplateHeading = (section: DocumentSectionSpec) => {
  const heading = $setState(
    $createTemplateHeadingNode(),
    templateSectionState,
    { ...section, Lines: [] },
  );
  return section.Title
    ? heading.append($createTextNode(section.Title))
    : heading;
};

// A section is its heading, its guidance and its lines; a field of a
// summary or an evaluation (`fields`) its heading and its AI instruction.
export const $createTemplateSection = (
  section: DocumentSectionSpec,
  fields: boolean,
): LexicalNode[] => [
  $createTemplateHeading(section),
  ...$createGuidanceLines(section.Instructions, fields),
  ...(fields ? [] : section.Lines.map($createTemplateLine)),
];

export type TemplateImportOptions = {
  // A summary's or an evaluation's fields.
  fields?: boolean;
};

// The template as one document of headings and paragraphs; every section,
// line and block keeps its id.
export const $importTemplate = (
  sections: DocumentSectionSpec[],
  { fields = false }: TemplateImportOptions = {},
) => {
  const root = $getRoot();
  root.clear();
  root.append(
    ...sections.flatMap((section) => $createTemplateSection(section, fields)),
  );
  if (root.isEmpty()) {
    root.append($createTemplateParagraphNode());
  }
};

export const $readTemplateSection = (heading: TemplateHeadingNode) =>
  $getState(heading, templateSectionState);

export const $readTemplateLine = (paragraph: TemplateParagraphNode) =>
  $getState(paragraph, templateLineState);

export const $readTemplateWording = (node: LexicalNode) =>
  $getState(node, templateWordingState);

// A paragraph Lexical made, as Enter does, is a new line.
export const $ensureTemplateLine = (paragraph: TemplateParagraphNode) => {
  const line = $readTemplateLine(paragraph);
  if (line) {
    return line;
  }
  const created = createTemplateLineDef();
  $setState(paragraph, templateLineState, created);
  return created;
};

export const $updateTemplateSection = (
  heading: TemplateHeadingNode,
  patch: Partial<DocumentSectionSpec>,
) =>
  $setState(heading, templateSectionState, (current) =>
    current ? { ...current, ...patch } : current,
  );

export const $setTemplateLineShowWhen = (
  paragraph: TemplateParagraphNode,
  ShowWhen: TemplateLineDef['ShowWhen'],
) =>
  $setState(paragraph, templateLineState, (current) => ({
    ...(current ?? createTemplateLineDef()),
    ShowWhen,
  }));

export const $isTemplateSubheading = (node: LexicalNode | null) =>
  $isTemplateParagraphNode(node) && !!$readTemplateLine(node)?.Subheading;

// A subheading holds text alone and is always shown, as scribe requires.
export const $setTemplateLineSubheading = (
  paragraph: TemplateParagraphNode,
  subheading: boolean,
) =>
  $setState(paragraph, templateLineState, (current) => ({
    ...(current ?? createTemplateLineDef()),
    ...(subheading && { ShowWhen: [] }),
    Subheading: subheading || undefined,
  }));

export type TemplateSectionNodes = {
  heading: TemplateHeadingNode | null;
  // The section's lines, and whatever else sits between its heading and the
  // next one.
  body: LexicalNode[];
};

// The document as its sections; what comes before the first heading is a
// section without one.
export const $listTemplateSections = () =>
  $getRoot()
    .getChildren()
    .reduce<TemplateSectionNodes[]>((sections, node) => {
      if ($isTemplateHeadingNode(node)) {
        return [...sections, { heading: node, body: [] }];
      }
      const last = sections.at(-1);
      return last
        ? [...sections.slice(0, -1), { ...last, body: [...last.body, node] }]
        : [{ heading: null, body: [node] }];
    }, []);
